import { Navigate, Route } from 'react-router-dom'

import ChecklistPreview from '@/components/BuildingDashboard/Checklists/Tabs/Dashboard/ChecklistPreview/ChecklistPreview'
import SocialLink from '@/components/BuildingDashboard/SocialLink/SocialLink'
import Links from '@/components/BuildingDashboard/SocialLink/Tabs/Links/Links'
import { ABILITIES } from '@/components/Can'
import CreateAmenityForm from '@/components/Reservations/ReservationsAmenities/AmenityForms/CreateAmenityForm'
import EditAmenityForm from '@/components/Reservations/ReservationsAmenities/AmenityForms/EditAmenityFrom'
import withRoleProtection from '@/components/withRoleProtection'
import { HomeScreenPage } from '@/pages/building-dashboard/home-screen'
import { ResidentProfileSettingsPage } from '@/pages/building-dashboard/resident-profile-settings'
import { ChecklistsDashboardPage, ChecklistsPage, ChecklistsTaskPage } from '@/pages/checklists'
import { FinancialsPage } from '@/pages/communication/financials'
import { SocialLinksPage } from '@/pages/communication/social-links'
import { CommunityEssentialsPage } from '@/pages/community-essentials/community-essentials.page'
import { GiftCardsLegacyPage, GiftCardsPage } from '@/pages/gift-cards'
import { CommentsPage } from '@/pages/groups/comments/comments.page'
import { GroupsPage } from '@/pages/groups/groups.page'
import { GroupsFormPage } from '@/pages/groups/groups-form/groups-form.page'
import { PostsPage } from '@/pages/groups/posts/posts.page'
import { GroupsSettingsPage } from '@/pages/groups/settings/group-settings.page'
import { MaintenanceRequestsPage } from '@/pages/maintenance/maintenance-requests'
import { MaintenanceStaffPage } from '@/pages/maintenance/maintenance-staff'
import {
  MarketShopDashboardPage,
  MarketShopInventoryPage,
  MarketShopProductPage,
  MarketShopSettingsPage,
  MarketShopTransactionsPage,
} from '@/pages/market-shop-dashboard'
import { PackagesPage } from '@/pages/packages'
import { PackageLogPage } from '@/pages/packages/package-log'
import { PackageSessionDetailsPage } from '@/pages/packages/package-session-details'
import { PackagesListPage } from '@/pages/packages/packages-list'
import {
  ParkingManagementPage,
  ResidentsParkingPage,
  VisitorsParkingPage,
} from '@/pages/parking-management'
import { NewReservationsGuard } from '@/pages/reservations/new-reservations-guard'
import { ReservationDetailsPage } from '@/pages/reservations/reservation-details/reservation-details.page'
import { VisitorsDashboardPage } from '@/pages/visitors-dashboard'
import { VisitorsGuestsPage } from '@/pages/visitors-dashboard/visitors-guests'
import { VisitorsHistoryPage } from '@/pages/visitors-dashboard/visitors-history'
import { VisitorsVendorsPage } from '@/pages/visitors-dashboard/visitors-vendors'
import { WebsiteWidgetsPage } from '@/pages/website-widgets/website-widgets.page'

import AssetTracking from '../../components/AssetTracking/AssetTracking'
import ActionLog from '../../components/BuildingDashboard/ActionLog/ActionLog'
import BuildingDashboard from '../../components/BuildingDashboard/BuildingDashboard'
import ChecklistsSettings from '../../components/BuildingDashboard/Checklists/Tabs/Settings/ChecklistsSettings'
import DigitalSignage from '../../components/BuildingDashboard/DigitalSignage/DigitalSignage'
import HomeScreen from '../../components/BuildingDashboard/HomeScreen/HomeScreen'
import Instructions from '../../components/BuildingDashboard/Instructions/Instructions'
import ManagementUsersPage from '../../components/BuildingDashboard/ManagementUsers/ManagementUsersPage'
import NeighborhoodPerks from '../../components/BuildingDashboard/NeighborhoodPerks/NeighborhoodPerks'
import Rewards from '../../components/BuildingDashboard/Rewards/Rewards'
import RewardsSettings from '../../components/BuildingDashboard/Rewards/RewardsSettings/RewardsSettings'
import RewardsDashboard from '../../components/BuildingDashboard/Rewards/Tabs/RewardsDashboard/RewardsDashboard'
import RewardsLoyalty from '../../components/BuildingDashboard/Rewards/Tabs/RewardsLoyalty/RewardsLoyalty'
import FrontDesk from '../../components/FrontDesk/FrontDesk'
import IncidentReports from '../../components/IncidentReports/IncidentReports'
import InspectionTemplateView from '../../components/InternalInspections/InspectionTemplateView/InspectionTemplateView'
import InternalInspectionsDetails from '../../components/InternalInspections/InternalInspectionDetails/InternalInspectionsDetails'
import InternalInspections from '../../components/InternalInspections/InternalInspections'
import InternalInspectionsDashboard from '../../components/InternalInspections/InternalInspectionsDashboard'
import InternalInspectionsRecurring from '../../components/InternalInspections/InternalInspectionsRecurring'
import InternalInspectionsTemplates from '../../components/InternalInspections/InternalInspectionsTemplates'
import ScheduleInspectionView from '../../components/InternalInspections/ScheduleInspectionView/ScheduleInspectionView'
import MaintenancePage from '../../components/Maintenance/MaintenancePage'
import MaintenanceRequests from '../../components/Maintenance/MaintenanceRequests/MaintenanceRequests'
import MaintenanceStaff from '../../components/Maintenance/MaintenanceStaff/MaintenanceStaff'
import ReputationManagement from '../../components/Organisation/ReputationManagement/ReputationManagement'
import ParkingDashboard from '../../components/ParkingDashboard/ParkingDashboard'
import DetailedView from '../../components/Reservations/DetailedView/DetailedView'
import Reservations from '../../components/Reservations/Reservations'
import ReservationsAmenities from '../../components/Reservations/ReservationsAmenities/ReservationsAmenities'
import ReservationsBookings from '../../components/Reservations/ReservationsBookings/ReservationsBookings'
import RestaurantManager from '../../components/RestaurantManager/RestaurantManager'
import WorkOrdersOffice from '../../components/WorkOrdersOffice/WorkOrdersOffice'

const ProtectedIncidentReports = withRoleProtection(
  IncidentReports,
  ['PropertyManager', 'Administrator', 'ShiftManager'],
  '/dashboard/calendar',
)
const ProtectedAssetTracking = withRoleProtection(
  AssetTracking,
  ['PropertyManager', 'Administrator', 'ShiftManager'],
  '/dashboard/calendar',
)
const ProtectedFrontDesk = withRoleProtection(
  FrontDesk,
  ['PropertyManager', 'Administrator', 'ShiftManager'],
  '/dashboard/calendar',
)
const ProtectedRestaurantManager = withRoleProtection(
  RestaurantManager,
  ['PropertyManager', 'Administrator', 'ShiftManager'],
  '/dashboard/calendar',
)
const ProtectedWorkOrdersOffice = withRoleProtection(
  WorkOrdersOffice,
  ['PropertyManager', 'Administrator', 'ShiftManager'],
  '/dashboard/calendar',
)

// Note: gates the whole Visitors subtree (guests/vendors/history) (EL-12843). Reuses the
// navbar's own ability so the route never rejects a role the Operations menu links in.
const ProtectedVisitors = withRoleProtection(
  VisitorsDashboardPage,
  ABILITIES.Navbar.visitors,
  '/dashboard/calendar',
)
const ProtectedWebsiteWidgets = withRoleProtection(
  WebsiteWidgetsPage,
  ['PropertyManager', 'Administrator', 'Manager'],
  '/dashboard/calendar',
)

export const buildingDashboardRoutesConfig = () => (
  <Route path="building-dashboard/*" element={<BuildingDashboard />}>
    <Route path="management-users/:buildingId?/:userId?" element={<ManagementUsersPage />} />

    <Route path="reservations" element={<Reservations />}>
      <Route index element={<Navigate replace to="bookings/reservations-list" />} />
      <Route path="bookings/reservations-list" element={<ReservationsBookings />} />

      <Route path="amenities">
        <Route index element={<ReservationsAmenities />} />
        <Route path="create" element={<CreateAmenityForm />} />
        <Route path="edit/:amenityId" element={<EditAmenityForm />} />
        <Route
          path="*"
          element={<Navigate replace to="/dashboard/building-dashboard/reservations/amenities" />}
        />
      </Route>
      {/* @ts-ignore – this route is old component without TS */}
      <Route path="detailed-view/:from/:to/:reservationId?" element={<DetailedView />} />
    </Route>

    <Route element={<NewReservationsGuard />}>
      <Route path="reservations">
        <Route path=":reservationId" element={<ReservationDetailsPage />} />
      </Route>
    </Route>

    <Route path="maintenance" element={<MaintenanceRequestsPage />} />
    <Route path="maintenance/requests">
      <Route index element={<MaintenanceRequestsPage />} />
      <Route path=":buildingId" element={<MaintenanceRequestsPage />} />
      <Route path=":buildingId/:requestId?" element={<MaintenanceRequestsPage />} />
    </Route>
    {/* Note: old design, kept reachable per EL-14186 rework - toggleable from the new Requests
    page. Staff nested here too so the old toolbar's own Staff tab stays in old-design chrome
    instead of jumping out to the new minimal Staff page. */}
    {/* @ts-ignore – this route is old component without TS */}
    <Route path="maintenance/legacy" element={<MaintenancePage />}>
      <Route index element={<MaintenanceRequests />} />
      <Route path="staff">
        <Route index element={<MaintenanceStaff />} />
        <Route path=":buildingId" element={<MaintenanceStaff />} />
        <Route path=":buildingId/:staffId?" element={<MaintenanceStaff />} />
      </Route>
      <Route path=":buildingId" element={<MaintenanceRequests />} />
      <Route path=":buildingId/:requestId?" element={<MaintenanceRequests />} />
    </Route>
    <Route path="maintenance/staff">
      <Route index element={<MaintenanceStaffPage />} />
      <Route path=":buildingId" element={<MaintenanceStaffPage />} />
      <Route path=":buildingId/:staffId?" element={<MaintenanceStaffPage />} />
    </Route>

    <Route path="visitors" element={<ProtectedVisitors />}>
      <Route index element={<Navigate replace to="guests" />} />
      <Route path="guests" element={<VisitorsGuestsPage />} />
      <Route path="vendors" element={<VisitorsVendorsPage />} />
      <Route path="history" element={<VisitorsHistoryPage />} />
    </Route>

    <Route path="packages" element={<PackagesPage />}>
      <Route index element={<Navigate replace to="list" />} />
      <Route path="list" element={<PackagesListPage />} />
      <Route path="log" element={<PackageLogPage />} />
      <Route path="log/:sessionId" element={<PackageSessionDetailsPage />} />
    </Route>

    <Route path="market-shop" element={<MarketShopDashboardPage />}>
      <Route index element={<Navigate replace to="inventory" />} />
      <Route path="inventory" element={<MarketShopInventoryPage />} />
      <Route path="inventory/add" element={<MarketShopProductPage />} />
      <Route path="inventory/:id" element={<MarketShopProductPage />} />
      <Route path="orders" element={<MarketShopTransactionsPage />} />
      <Route path="settings" element={<MarketShopSettingsPage />} />
    </Route>

    <Route path="internal-inspections" element={<InternalInspections />}>
      <Route index element={<Navigate replace to="dashboard" />} />
      <Route path="dashboard/:buildingId?" element={<InternalInspectionsDashboard />} />
      <Route path="setup/recurring/:buildingId?" element={<InternalInspectionsRecurring />} />
      <Route path="setup/templates/:buildingId?" element={<InternalInspectionsTemplates />} />
      <Route path="template/:action/:templateId?" element={<InspectionTemplateView />} />
      <Route path="details/:inspectionId" element={<InternalInspectionsDetails />} />
      <Route path="edit/:inspectionId" element={<ScheduleInspectionView />} />
      <Route path=":trigger/create" element={<ScheduleInspectionView />} />
    </Route>

    <Route path="groups" element={<GroupsPage />}>
      <Route path="posts/:postId?/:groupId?" element={<PostsPage />} />
      <Route path="comments" element={<CommentsPage />} />
    </Route>
    <Route path="groups-settings">
      <Route path="" element={<GroupsSettingsPage />} />
      <Route path="create" element={<GroupsFormPage />} />
      <Route path="edit/:groupId" element={<GroupsFormPage />} />
    </Route>

    {/* // test */}
    {/* <Route path="groups" element={<Groups />}>
      <Route index element={<Navigate replace to="dashboard" />} />
      <Route path="dashboard" element={<GroupPostsDashboard />}>
        <Route path=":groupId" element={<GroupPostsDashboard />}>
          <Route path="post/:postId" element={<GroupPostsDashboard />} />
        </Route>
      </Route>
      <Route path="settings" element={<GroupsSettings />}>
        <Route path=":groupId" element={<GroupsSettings />} />
      </Route>
      <Route path="create" element={<GroupForm />} />
      <Route path="edit/:groupId" element={<GroupForm />} />
    </Route> */}

    <Route path="parking-management" element={<ParkingManagementPage />}>
      <Route index element={<Navigate replace to="residents" />} />
      <Route path="residents" element={<ResidentsParkingPage />} />
      <Route path="visitors" element={<VisitorsParkingPage />} />
    </Route>

    {/* @ts-ignore – this route is old component without TS */}
    <Route path="rewards" element={<Rewards />}>
      <Route index element={<Navigate replace to="dashboard" />} />
      <Route path="dashboard">
        <Route index element={<RewardsDashboard />} />
        <Route path=":buildingId" element={<RewardsDashboard />} />
      </Route>
      <Route path="gift-cards">
        <Route index element={<GiftCardsPage />} />
        <Route path="legacy">
          <Route index element={<GiftCardsLegacyPage />} />
          <Route path=":buildingId" element={<GiftCardsLegacyPage />} />
        </Route>
        <Route path=":buildingId" element={<GiftCardsPage />} />
      </Route>
      <Route path="loyalty">
        <Route index element={<RewardsLoyalty />} />
        <Route path=":buildingId" element={<RewardsLoyalty />} />
      </Route>
      <Route path="settings">
        <Route index element={<RewardsSettings />} />
        <Route path=":buildingId" element={<RewardsSettings />} />
      </Route>
    </Route>
    <Route path="financials" element={<FinancialsPage />} />

    <Route path="reputation-management" element={<ReputationManagement />} />

    <Route path="social-links" element={<SocialLinksPage />} />
    <Route
      path="social-links/legacy"
      element={
        <SocialLink>
          <Links />
        </SocialLink>
      }
    />

    <Route path="digital-signage" element={<DigitalSignage />} />

    <Route path="checklists" element={<ChecklistsPage />}>
      <Route index element={<ChecklistsDashboardPage />} />
      <Route path="task/:taskId" element={<ChecklistsTaskPage />} />
      {/* TODO: Replace with new component, currently it was decided to not refactor the inspection preview page */}
      <Route
        path="inspection/:inspectionId"
        element={<ChecklistPreview data={null} onRemoved={null} />}
      />
      {/* Note: Settings use old components, for now refactored is not planned  */}
      <Route path="settings">
        <Route index element={<ChecklistsSettings />} />
        <Route path=":buildingId" element={<ChecklistsSettings />} />
      </Route>
    </Route>

    <Route path="action-log/:buildingId?" element={<ActionLog />} />
    <Route path="instructions/:buildingId?" element={<Instructions />} />
    <Route path="home-screen/:buildingId?" element={<HomeScreenPage />} />
    <Route path="home-screen/:buildingId?/legacy" element={<HomeScreen />} />
    <Route path="parking" element={<ParkingDashboard />} />
    <Route path="building-essentials" element={<CommunityEssentialsPage />} />
    <Route path="resident-profile-settings" element={<ResidentProfileSettingsPage />} />
    <Route path="neighborhood-perks" element={<NeighborhoodPerks />} />

    <Route path="incident-reports" element={<ProtectedIncidentReports />} />
    <Route path="asset-tracking" element={<ProtectedAssetTracking />} />
    <Route path="website-widgets" element={<ProtectedWebsiteWidgets />} />
    <Route path="front-desk" element={<ProtectedFrontDesk />} />
    <Route path="restaurant-manager" element={<ProtectedRestaurantManager />} />
    <Route path="work-orders-office" element={<ProtectedWorkOrdersOffice />} />
  </Route>
)
